import CardCommon from '@op/components/CardCommon';
import { barIcon, lineIcon, stackLineIcon, tableIcon } from '@op/constants/chart';
import React, { useMemo, useState } from 'react';
import MetricsCard from '../MetricsCard';
import { dealWithOthersName } from '@op/utils/languageValue';
import './index.scss';
import Table from './Table';
import Chart from './Chart';
import { formatDateFunc, formatTimeToShow } from '@op/utils/time';
import useLanguage from '@op/hooks/useLanguage';
import { NO_DATE } from '@op/constants';
import { downloadAction } from '@op/pages/download/utils';
import { IRealtimneChannelLanguageChart } from '../../type';
import { channelInfo } from '@op/components/ChannelLabel';
import { useRealtimeChannel, useRealtimeLanguage } from '../../hook';
import { METRICS_LIST, REALTIME_CHANNEL_KEY_VALUE, REALTIME_CHANNEL_LANGUAGE_SORT } from '../../constant';
import { DBO_TM } from '@op/utils/format';
export default function ChannelLanguageChart(props: IRealtimneChannelLanguageChart) {
  const { gameInfo, title, metrics, formatValue, filter, isPercent = false, isAvg = false, type, height = 468, organization, reviewTypes, deadline } = props;
  const { formatMessage } = useLanguage();
  const isToday = useMemo(() => DBO_TM.isToday(filter), [filter]);
  const header = type === 'channel' ? 'Channel' : 'Language';
  const [chartType, setChartType] = useState('line');

  const filterParams = useMemo(() => {
    let reviewValue: string[] = [];

    if (REALTIME_CHANNEL_KEY_VALUE?.[reviewTypes]) {
      reviewValue = REALTIME_CHANNEL_KEY_VALUE[reviewTypes];
    }
    const sort_item = REALTIME_CHANNEL_LANGUAGE_SORT[metrics] || metrics;
    if (reviewTypes === 'stream') {
      if (isToday) {
        return {
          ...filter,
          ...gameInfo,
          date_type: 'hourly',
          need_link: true,
          need_many_hour_ago: isToday,
          many_hour_ago: deadline === 'last_hour' ? 1 : 0
        };
      }
      return {
        ...filter,
        ...gameInfo,
        date_type: 'hourly',
        need_link: true,
        need_many_hour_ago: false,
      };
    }
    if (isToday) {
      return {
        ...filter,
        ...gameInfo,
        date_type: 'hourly',
        top_n: 5,
        need_link: true,
        organization: organization === 'all' ? '' : organization,
        review_types: reviewValue,
        sort_item,
        sort_model: 'desc',
        need_many_hour_ago: isToday,
        many_hour_ago: deadline === 'last_hour' ? 1 : 0
      };
    }
    return {
      ...filter,
      ...gameInfo,
      date_type: 'hourly',
      top_n: 5,
      need_link: true,
      need_many_hour_ago: false,
      organization: organization === 'all' ? '' : organization,
      review_types: reviewValue,
      sort_item,
      sort_model: 'desc'
    };
  }, [filter, gameInfo, organization, reviewTypes, metrics, deadline]);

  const hookToUse = useMemo(() => type === 'language' ? useRealtimeLanguage : useRealtimeChannel, [type]);
  const { data, loading } = hookToUse({
    filter: filterParams,
    extraData: {
      metricsKey: reviewTypes === 'stream' ? METRICS_LIST.stream : METRICS_LIST.language
    },
    isStream: reviewTypes === 'stream'
  });

  const { oldTime, newTime, topList = [], chartData, tableData, isEmpty, timeline_of_start_time, timeline_of_end_time } = useMemo(() => {
    const { link_of_start_time, link_of_end_time, timeline_of_start_time, timeline_of_end_time, topList } = data || {};
    return {
      oldTime: [link_of_start_time, link_of_end_time],
      newTime: [timeline_of_start_time, timeline_of_end_time],
      isEmpty: !topList?.some((val) => val?.[metrics] > 0),
      topList,
      ...data
    };
  }, [data]);

  const formatTitle = (val: { name: string; color: string; }, type: 'channel' | 'language', isAvg?: boolean) => {
    const { name, color } = val;
    if (name === 'Total') {
      return isAvg ? formatMessage({ id: 'Average' }) : formatMessage({ id: 'Total-3' });
    }
    if (type === 'channel') {
      const { img, name: channelName } = channelInfo(name);
      const titleWithIcon = (
        <div className="channel-language-div" style={{ ['--bar-color' as any]: color }}>
          <img src={img} />
          <span className="channel-language-span">
            {channelName}
          </span>
        </div>
      );

      return titleWithIcon;
    }
    return (
      <span className="channel-language-div" style={{ ['--bar-color' as any]: color }}>
        {dealWithOthersName(name, type)}
      </span>
    );
  };

  return (
    <CardCommon
      // contentCard
      operationInfo={[chartType, setChartType]}
      operations={[tableIcon, lineIcon, stackLineIcon, barIcon]}
      hasDownload={!isEmpty}
      empty={isEmpty}
      className='dbo-realtime-channel-language-chart-wrapper'
      loading={loading}
      loadingHeight={height}
      emptyHeight={height}
      title={(
        <div className="realtime-header">
          <span className='realtime-title'>
            {formatMessage({ id: title || NO_DATE.EMPTY })}
          </span>
          {
            !loading && (
              <span className='realtime-subtitle'>
                {formatTimeToShow({
                  start_time: timeline_of_start_time,
                  end_time: timeline_of_end_time,
                  date_type: 'hourly'
                })}
              </span>
            )
          }
        </div>
      )}
      handleDownloadOnClick={() => {
        downloadAction({
          key: 'RealtimeChannelLanguagePerformanceDownload',
          fileName: `Realtime ${header} Performance - ${title}`,
          filter: {
            ...filter,
            ...gameInfo,
          },
          auxiliaryParams: {
            metrics,
            type,
            title
          },
          data: tableData
        });
      }}
    // logBtnId="KMCM"
    // extraInfo={{ tab, keyMetric: keyMetric.value, byType: byType.value }}
    >
      <div
        className='dbo-realtime-channel-language-top-list'
      >
        {topList.map((val: { name: string; color: string; }, index: number) => {
          const title = formatTitle(val, type, isAvg);
          return (
            <MetricsCard
              key={index}
              data={{
                ...val,
                oldTime,
                time: newTime
              }}
              tabItem={{
                title,
                valueKey: metrics,
                formatValue,
                formatDate: (value: string) => formatDateFunc(filter?.date_type || 'hourly')(value)
              }}
              classNames='top-list'
            />
          );
        })}
      </div>

      {chartType === 'table' ? (
        <Table
          filter={filter}
          formatValue={formatValue}
          metrics={metrics}
          title={title}
          header={header}
          data={tableData}
        />
      ) : (
        <Chart
          chartType={chartType}
          title={title}
          data={chartData}
          type={type}
          metrics={metrics}
          filter={filterParams}
          formatValue={formatValue}
          isPercent={isPercent}
        />
      )}
    </CardCommon>
  );
}